import React, { useState, InputHTMLAttributes } from "react";
import { BsFillEyeFill, BsFillEyeSlashFill } from "react-icons/bs";
import { FieldError } from "react-hook-form";
import { Error } from "./Error";
import { Label } from "./Label";
import { cn } from "@/lib/utils";

const buttonVariants = {
  default: "block py-4 px-3 w-full border bg-white rounded-sm",
  secondary:
    "block py-2 px-2 w-full border bg-white rounded-lg placeholder:text-sm text-sm font-medium text-dark",
};

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string | FieldError;
  variant?: keyof typeof buttonVariants;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const {
      label,
      id,
      type,
      required,
      error,
      variant = "default",
      placeholder,
      disabled,
      className,
      ...rest
    } = props;

    const [isViewPassword, setIsViewPassword] = useState<boolean>(false);

    const togglePasswordVisibility = () => {
      setIsViewPassword((prevState) => !prevState);
    };

    const getInputClassName = () => {
      const baseClass = buttonVariants[variant];
      return cn(
        baseClass,
        error
          ? "border-danger focus:outline-danger"
          : "border-gray-200 focus:outline-secondary",
        disabled && "bg-gray-100 text-gray-500 cursor-not-allowed opacity-85",
        className
      );
    };

    const renderPasswordInput = () => (
      <div className="relative">
        <input
          id={id}
          {...rest}
          ref={ref}
          disabled={disabled}
          type={isViewPassword ? "text" : "password"}
          className={getInputClassName()}
        />
        <button
          className={cn(
            "absolute right-2 top-5",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onClick={togglePasswordVisibility}
          type="button"
          disabled={disabled}
        >
          {isViewPassword ? (
            <BsFillEyeSlashFill className="text-grayMedium" fontSize={22} />
          ) : (
            <BsFillEyeFill className="text-grayMedium" fontSize={22} />
          )}
        </button>
      </div>
    );

    return (
      <>
        {label && <Label htmlFor={id} required={required} label={label} />}
        {type === "password" ? (
          renderPasswordInput()
        ) : (
          <input
            id={id}
            {...rest}
            ref={ref}
            disabled={disabled}
            type={type}
            placeholder={placeholder}
            className={getInputClassName()}
          />
        )}
        {variant === "default" && <Error error={error} />}
      </>
    );
  }
);

Input.displayName = "Input";
